
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ITrainingSsoResponse, IUserCertification } from "@app/account/account.models";
import { Portals } from "@app/shared/shared.models";
import { IAppState } from "@app/store";
import { WarningToast } from "@app/store/toast/toast.actions";
import { IProctorUStudent, IProctorUTimeZone, ITechnicianSummaryModel, ITrainingSchedule, LmsUrls } from "@app/training/training.models";
import { environment } from "@env/environment";
import { Store } from "@ngrx/store";
import { map } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class TrainingService {
	constructor(private http: HttpClient, private readonly store: Store<IAppState>) {

	}

	getCourseEnrollmentUrl(courseId: string) {
		return this.http.get<ITrainingSsoResponse>("api/training/GetCourseEnrollmentUrl", { params: { courseId } });
	}

	getLearningPathEnrollmentUrl(pathId: string) {
		return this.http.get<ITrainingSsoResponse>("api/training/GetLearningPathEnrollmentUrl", { params: { pathId } });
	}

	getAutomationProgrammerSchedule() {
		return this.http.get<ITrainingSchedule>("api/training/GetAutomationProgrammerSchedule");
	}

	getAccountCertifications() {
		return this.http.get<IUserCertification[]>("api/Training/GetAccountCertifications");
	}

	getTechnicianSummary() {
		return this.http.get<ITechnicianSummaryModel>("api/training/GetTechnicianSummary")
			.pipe(
				map(technician => ({
					...technician,
					certifications: technician.certifications.map(item => ({
						...item,
						isExpireWithin3Months: item.expiryDate ? this.areDatesWithinThreeMonths(new Date(), new Date(item.expiryDate)) : false,
						isExpired: item.expiryDate ? this.isCertificationExpired(new Date(), new Date(item.expiryDate)) : false
					})),
				})));
	}

	//check if certification is expire or not
	isCertificationExpired(currDate: Date, certExpireDate: Date): boolean {
		const d1 = new Date(currDate.getFullYear(), currDate.getMonth(), currDate.getDate());
		const d2 = new Date(certExpireDate.getFullYear(), certExpireDate.getMonth(), certExpireDate.getDate());

		return d1.getTime() > d2.getTime();
	}

	//chcek if expiredate is near by to 3 months
	areDatesWithinThreeMonths(currDate: Date, certExpireDate: Date): boolean {
		// Ensure currDate is the earlier date
		const earlierDate = currDate < certExpireDate ? currDate : certExpireDate;
		const laterDate = currDate > certExpireDate ? currDate : certExpireDate;

		// Create a copy of the earlier date and add 3 months
		const threeMonthsLater = new Date(earlierDate);
		threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);

		// Compare expire date with current
		return laterDate <= threeMonthsLater;
	}

	getStudentInformation() {
		return this.http.get<IProctorUStudent>("api/training/GetStudentProfile");
	}

	createStudentProfile(student: IProctorUStudent) {
		return this.http.post<boolean>("api/training/CreateStudentProfile", student);
	}

	initiateProctorSso(openedTab: Window) {
		this.http.get("api/training/GetProctorSsoUrl", { responseType: "text" }).subscribe(result => {
			if (result) {
				openedTab.location.href = result;
				return true;
			} else {
				this.store.dispatch(new WarningToast({ message: "There was a problem redirecting you to the exam scheduler. Please try again later or contact the Sales Support Team for assistance." }));
				openedTab.close();
				return false;
			}
		});
	}
	initiateProctorSsoc4ct(openedTab: Window) {
		this.http.get("api/training/GetProctorSsoUrlC4CT", { responseType: "text" }).subscribe(result => {
			if (result) {
				openedTab.location.href = result;
				return true;
			} else {
				this.store.dispatch(new WarningToast({ message: "There was a problem redirecting you to the exam scheduler. Please try again or contact the Sales Support Team for assistance." }));
				openedTab.close();
				return false;
			}
		});
	}

	initiateProctorSsoRecertc4ct(openedTab: Window) {
		this.http.get("api/training/GetProctorSsoUrlRecertC4CT", { responseType: "text" }).subscribe(result => {
			if (result) {
				openedTab.location.href = result;
				return true;
			} else {
				this.store.dispatch(new WarningToast({ message: "There was a problem redirecting you to the exam scheduler. Please try again or contact the Sales Support Team for assistance." }));
				openedTab.close();
				return false;
			}
		});
	}
	getTimeZones() {
		return this.http.get<IProctorUTimeZone[]>("api/address/GetTimeZones");
	}

	transformTrainingUrl(url: string, portal: Portals): string {
		if (environment.dev) {
			url = "/saml/training";
		} else {
			const portalLmsHost = LmsUrls.get(portal);
			url = url.replace("control4.learnupon.com", portalLmsHost)
				.replace("training.control4.com", portalLmsHost)
				.replace("education.control4.com", portalLmsHost);
		}
		return url;
	}
}